import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Landmark, AlertTriangle, Check, X } from "lucide-react";

const eur = (v) => Number(v || 0).toLocaleString("it-IT", { style: "currency", currency: "EUR" });
const dateIT = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT", { timeZone: "Europe/Rome" }) : "—");

const ROLE_ROWS = [
  { k: "collaboratore", l: "Collaboratori" },
  { k: "interno", l: "Utenze interne" },
  { k: "consultazione", l: "Utenze di consultazione" },
];
const FEATURE_ROWS = [
  { k: "plan_includes_agent_flow", l: "Flusso di cassa agente" },
  { k: "plan_includes_collab_flow_module", l: "Modulo flusso collaboratori" },
  { k: "plan_includes_plurimandatari", l: "Plurimandatari (multi-compagnia)" },
];

export const BonificoInfo = ({ bonifico }) => (
  <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm space-y-1" data-testid="bonifico-info">
    <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1 flex items-center gap-1.5"><Landmark className="w-3.5 h-3.5" /> Coordinate per il bonifico</div>
    <div>Intestatario: <strong>{bonifico?.intestatario}</strong></div>
    <div>IBAN: <strong className="font-mono-num" data-testid="bonifico-iban">{bonifico?.iban}</strong></div>
    {bonifico?.banca && <div>Banca: <strong>{bonifico.banca}</strong></div>}
    <div>Causale: <strong>{bonifico?.causale}</strong></div>
  </div>
);

export const CustomPlanAbbonamento = ({ plan, ag }) => {
  const bs = plan.billing_status || {};
  const period = plan.periodicita === "annuale" ? "anno" : "mese";
  return (
    <div className="space-y-6 max-w-5xl" data-testid="custom-plan-abbonamento">
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Pagamenti</div>
        <h1 className="font-heading text-3xl font-bold text-slate-900">Abbonamento</h1>
      </div>
      <Card className="p-6 border-slate-200 space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-wider text-blue-600 font-semibold">Piano corrente</div>
            <h2 className="font-heading text-2xl font-bold text-slate-900 mt-1" data-testid="current-plan-name">{plan.plan_name}</h2>
            <div className="text-sm text-slate-600 mt-1">
              <span className="font-semibold" data-testid="custom-plan-price">{eur(plan.price_net)} + IVA / {period}</span>
              <span className="text-xs text-slate-500 ml-2">({eur(plan.price_total)} IVA inclusa) · pagamento tramite bonifico</span>
            </div>
            {plan.retention_label && <div className="text-xs text-slate-500 mt-1">Storico conservato: {plan.retention_label}</div>}
          </div>
          <Badge variant="outline" className={`rounded-full px-3 py-1 ${bs.bonifico_overdue ? "bg-amber-100 text-amber-800 border-amber-200" : plan.bonifico_pagato ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-slate-100 text-slate-700 border-slate-200"}`} data-testid="plan-status">
            {bs.bonifico_overdue ? "Canone scaduto" : plan.bonifico_pagato ? "Pagato" : "In attesa di pagamento"}
          </Badge>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="border border-slate-200 rounded-lg p-4">
            <div className="text-xs text-slate-500 uppercase font-semibold">Prossima scadenza</div>
            <div className="font-heading text-lg font-semibold mt-1" data-testid="custom-plan-due">{dateIT(ag.current_period_end)}</div>
          </div>
          <BonificoInfo bonifico={plan.bonifico} />
        </div>
        {bs.bonifico_overdue && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900 flex items-start gap-2" data-testid="custom-plan-overdue">
            <AlertTriangle className="w-4 h-4 mt-0.5" />
            Il canone risulta scaduto. Effettua il bonifico entro il {dateIT(bs.grace_period_ends_at)} per evitare la sospensione dell&apos;accesso.
          </div>
        )}
      </Card>
      <Card className="p-6 border-slate-200 grid md:grid-cols-2 gap-6">
        <div>
          <h3 className="font-heading text-lg font-semibold text-slate-900 mb-3">Utenze incluse</h3>
          {ROLE_ROWS.map((r) => {
            const max = plan.max_by_role?.[r.k];
            return (
              <div key={r.k} className="flex justify-between text-sm py-1.5 border-b border-slate-100" data-testid={`custom-limit-${r.k}`}>
                <span className="text-slate-600">{r.l}</span>
                <span className="font-mono-num font-semibold">{plan.usage_by_role?.[r.k] ?? 0} / {max === null || max === undefined ? "illimitate" : max}</span>
              </div>
            );
          })}
        </div>
        <div>
          <h3 className="font-heading text-lg font-semibold text-slate-900 mb-3">Funzionalità</h3>
          {FEATURE_ROWS.map((f) => (
            <div key={f.k} className="flex items-center gap-2 text-sm py-1.5">
              {plan[f.k] ? <Check className="w-4 h-4 text-emerald-600" /> : <X className="w-4 h-4 text-slate-300" />}
              <span className={plan[f.k] ? "text-slate-800" : "text-slate-400"}>{f.l}</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};
